import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { getUserWiseCourseProgress } from "../../../Services/trainingService"; 

const ProgressOverviewTable = ({ filters }) => {
  // ======== State inside component ========
  const [progressData, setProgressData] = useState([]);
  const [progressLoading, setProgressLoading] = useState(false);

  // ======== Fetch progress ========
  const fetchProgress = async (filters = {}) => {
    setProgressLoading(true);
    try {
      // Normalize filters
      const normalizedFilters = {
        ...filters,
        course: filters.course || filters.category || "",
      };

      const cleanedFilters = Object.fromEntries(
        Object.entries(normalizedFilters).filter(([_, v]) => v !== "")
      );

      // API call
      const res = await getUserWiseCourseProgress({
        user_id: cleanedFilters.user,
        course_id: cleanedFilters.course,
      });

      // Flatten response
      let flattenedData = res.data.flatMap((user) =>
        user.courses.map((course) => ({
          employeeName: user.user_name,
          course: course.course_name,
          courseId: course.course_id,
          progress: course.progress,
          status: course.status,
          completedModules: `${course.modules.completed}/${course.modules.total}`,
        }))
      );

      // Filter by course if provided
      if (filters.course) {
        flattenedData = flattenedData.filter(
          (row) => row.courseId === filters.course
        );
      }

      setProgressData(flattenedData);
      return flattenedData;
    } catch (e) {
      toast.error("Failed to load progress data");
      setProgressData([]);
    } finally {
      setProgressLoading(false);
    }
  };

  // ======== Fetch on filters change ========
  useEffect(() => {
    fetchProgress(filters);
  }, [filters]);

  // ======== Loading ========
  if (progressLoading) {
    return (
      <div className="text-center py-10 text-gray-500">Loading progress...</div>
    );
  }

  return (
    <div className="px-0 py-0">
      {/* ========== DESKTOP TABLE ========== */}
      <div className="hidden md:block">
        <table className="w-full border-collapse text-center">
          <thead className="bg-gray-50 sticky top-0 z-10 text-secondary shadow-md">
            <tr>
              <th className="px-4 py-4 text-left font-semibold">Employee</th>
              <th className="px-4 py-4 text-left font-semibold">Course</th>
              <th className="px-4 py-4 text-left font-semibold w-56">Progress</th>
              <th className="px-4 py-4 text-left font-semibold">Modules</th>
              <th className="px-4 py-4 text-left font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {progressData.map((row, index) => (
              <tr
                key={`${row.employeeName}-${row.course}`}
                className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
              >
                <td className="px-4 py-4 text-left text-secondary">{row.employeeName}</td>
                <td className="px-4 py-4 text-left text-secondary">{row.course}</td>

                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="flex-1 bg-gray-200 rounded-full h-2.5">
                      <div
                        className="bg-primary h-2.5 rounded-full transition-all"
                        style={{ width: `${row.progress}%` }}
                      />
                    </div>
                    <span className="text-xs text-secondary w-10 text-left">
                      {row.progress}%
                    </span>
                  </div>
                </td>

                <td className="px-4 py-3 text-left text-secondary">{row.completedModules}</td>

                <td className="px-4 py-3 text-left text-secondary">
                  <span
                    className={`px-2 py-1 text-left rounded-full font-medium whitespace-nowrap ${
                      row.status === "completed"
                        ? "bg-green-100 text-green-700"
                        : row.status === "in_progress"
                        ? "bg-yellow-100 text-yellow-700"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {row.status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ========== MOBILE CARDS ========== */}
      <div className="md:hidden">
        <div id="mobileProgressList" className="space-y-3 p-0">
          {progressData.map((row, index) => (
            <div key={index} className="bg-white shadow rounded-lg p-4">
              <div className="flex justify-between items-center mb-2">
                <p className="text-base font-semibold text-primary">{row.employeeName}</p>
                <span
                  className={`px-2 py-0.5 rounded-md text-xs font-medium ${
                    row.status === "completed"
                      ? "bg-green-100 text-green-700"
                      : row.status === "in_progress"
                      ? "bg-yellow-100 text-yellow-700"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {row.status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                </span>
              </div>

              <div className="flex justify-between items-center mb-3">
                <p className="text-sm font-medium text-gray-900">{row.course}</p>
                <p className="text-sm text-gray-600 font-medium">{row.completedModules}</p>
              </div>

              <div className="flex items-center gap-2">
                <div className="flex-1 bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-primary h-2 rounded-full transition-all"
                    style={{ width: `${row.progress}%` }}
                  />
                </div>
                <span className="text-xs font-semibold text-gray-700">{row.progress}%</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProgressOverviewTable;
